import React, { useEffect, useMemo } from 'react';
import { StepProps } from '../MultiStepModal';

export default function CertificateTemplateLowConfidenceReviewStep({ data, onDataChange }: StepProps) {
  const schema = (data as any)?.inferredSchema;
  const lowConfidenceFieldsRaw = (data as any)?.lowConfidenceFields;
  const lowConfidenceEditsRaw = (data as any)?.lowConfidenceEdits;
  const baseInferredSchema = (data as any)?.baseInferredSchema;
  const lowConfidenceFields: any[] = useMemo(() => {
    return Array.isArray(lowConfidenceFieldsRaw) ? lowConfidenceFieldsRaw : [];
  }, [lowConfidenceFieldsRaw]);

  const edits: Record<string, any> = useMemo(() => {
    return lowConfidenceEditsRaw && typeof lowConfidenceEditsRaw === 'object' ? lowConfidenceEditsRaw : {};
  }, [lowConfidenceEditsRaw]);

  useEffect(() => {
    if (!schema) return;
    if (baseInferredSchema) return;
    try {
      onDataChange({ baseInferredSchema: JSON.parse(JSON.stringify(schema)) });
    } catch {
      onDataChange({ baseInferredSchema: schema });
    }
  }, [schema, baseInferredSchema, onDataChange]);

  const fromTagBySignature = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of lowConfidenceFields) {
      const sig = String(f?.fieldSignature || '').trim();
      if (!sig) continue;
      const tag = String(f?.semanticTag || '').trim();
      m.set(sig, tag);
    }
    return m;
  }, [lowConfidenceFields]);

  const findFieldLocation = (schemaJson: any, fieldSignature: string) => {
    const screens: any[] = Array.isArray(schemaJson?.screens) ? schemaJson.screens : [];
    for (const s of screens) {
      const screenKey = String(s?.key || '').trim();
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      for (const sec of sections) {
        const sectionKey = String(sec?.key || '').trim();
        const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
        for (const f of fields) {
          const sig = String(f?.fieldSignature || '').trim();
          if (sig && sig === fieldSignature) {
            return {
              screenKey,
              sectionKey,
              semanticTag: String(f?.semanticTag || '').trim(),
              field: f,
            };
          }
        }
      }
    }
    return null;
  };

  const updateSchemaWithEdits = (
    schemaJson: any,
    fieldSignature: string,
    args: { semanticTag?: string | null; screenKey?: string | null; sectionKey?: string | null },
  ) => {
    if (!schemaJson || typeof schemaJson !== 'object') return schemaJson;

    const loc = findFieldLocation(schemaJson, fieldSignature);
    if (!loc) return schemaJson;

    const nextSemanticTag = args.semanticTag === undefined ? loc.semanticTag || null : args.semanticTag;
    const nextScreenKey = args.screenKey === undefined ? loc.screenKey : args.screenKey;
    const nextSectionKey = args.sectionKey === undefined ? loc.sectionKey : args.sectionKey;

    const screens: any[] = Array.isArray(schemaJson?.screens) ? schemaJson.screens : [];
    const newScreens = screens.map((s) => ({ ...s, sections: (Array.isArray(s?.sections) ? s.sections : []).map((sec: any) => ({ ...sec, fields: Array.isArray(sec?.fields) ? sec.fields.slice() : [] })) }));

    const removeFrom = () => {
      const s = newScreens.find((x) => String(x?.key || '').trim() === loc.screenKey);
      if (!s) return;
      const sec = (s.sections || []).find((x: any) => String(x?.key || '').trim() === loc.sectionKey);
      if (!sec) return;
      sec.fields = (sec.fields || []).filter((ff: any) => String(ff?.fieldSignature || '').trim() !== fieldSignature);
      s.sections = (s.sections || []).filter((x: any) => Array.isArray(x?.fields) && x.fields.length > 0);
    };

    const ensureTarget = () => {
      const sk = String(nextScreenKey || '').trim();
      const sek = String(nextSectionKey || '').trim() || sk;
      if (!sk) return null;

      let s = newScreens.find((x) => String(x?.key || '').trim() === sk);
      if (!s) {
        s = { key: sk, title: sk, sections: [] };
        newScreens.push(s);
      }

      let sec = (s.sections || []).find((x: any) => String(x?.key || '').trim() === sek);
      if (!sec) {
        sec = { key: sek, title: sek, fields: [] };
        s.sections = [...(s.sections || []), sec];
      }

      return { screen: s, section: sec };
    };

    const moving = String(nextScreenKey || '').trim() !== loc.screenKey || String(nextSectionKey || '').trim() !== loc.sectionKey;

    const updatedField = { ...loc.field, semanticTag: nextSemanticTag };

    if (moving) {
      removeFrom();
      const target = ensureTarget();
      if (target) {
        target.section.fields = [...(target.section.fields || []), updatedField];
      }
    } else {
      const s = newScreens.find((x) => String(x?.key || '').trim() === loc.screenKey);
      const sec = (s?.sections || []).find((x: any) => String(x?.key || '').trim() === loc.sectionKey);
      if (sec) {
        sec.fields = (sec.fields || []).map((ff: any) => {
          const sig = String(ff?.fieldSignature || '').trim();
          if (sig && sig === fieldSignature) return updatedField;
          return ff;
        });
      }
    }

    const cleanedScreens = newScreens.filter((s) => Array.isArray(s?.sections) && s.sections.length > 0);
    return { ...schemaJson, screens: cleanedScreens };
  };

  const rows = useMemo(() => {
    return lowConfidenceFields
      .map((f) => {
        const sig = String(f?.fieldSignature || '').trim();
        if (!sig) return null;
        const loc = findFieldLocation(schema, sig);
        const fromTag = String(loc?.semanticTag || f?.semanticTag || '').trim();
        const fromScreenKey = String(loc?.screenKey || '').trim();
        const fromSectionKey = String(loc?.sectionKey || '').trim();
        const e = edits?.[sig];
        const toTag = String((typeof e === 'string' ? e : e?.toSemanticTag) ?? fromTag).trim();
        const toScreenKey = String((typeof e === 'object' ? e?.toScreenKey : '') ?? '').trim() || fromScreenKey;
        const toSectionKey = String((typeof e === 'object' ? e?.toSectionKey : '') ?? '').trim() || fromSectionKey;
        return {
          pdfFieldName: String(f?.pdfFieldName || '').trim(),
          confidence: Number(f?.confidence ?? 0),
          fieldSignature: sig,
          fromSemanticTag: fromTag,
          toSemanticTag: toTag,
          fromScreenKey,
          toScreenKey,
          fromSectionKey,
          toSectionKey,
        };
      })
      .filter(Boolean) as any[];
  }, [lowConfidenceFields, edits, schema]);

  const changedCount = useMemo(() => {
    let n = 0;
    for (const r of rows) {
      const tagChanged =
        String(r.toSemanticTag || '').trim() && String(r.toSemanticTag || '').trim() !== String(r.fromSemanticTag || '').trim();
      const screenChanged = String(r.toScreenKey || '').trim() && String(r.toScreenKey || '').trim() !== String(r.fromScreenKey || '').trim();
      const sectionChanged = String(r.toSectionKey || '').trim() && String(r.toSectionKey || '').trim() !== String(r.fromSectionKey || '').trim();
      if (tagChanged || screenChanged || sectionChanged) n += 1;
    }
    return n;
  }, [rows]);

  const setTag = (fieldSignature: string, nextTag: string) => {
    const cleaned = String(nextTag || '').trim();
    const fromTag = String(fromTagBySignature.get(fieldSignature) || '').trim();

    const existing = edits?.[fieldSignature];
    const obj = typeof existing === 'object' && existing ? existing : {};
    const nextEdits = { ...edits, [fieldSignature]: { ...obj } };

    if (!cleaned || cleaned === fromTag) {
      delete nextEdits[fieldSignature]?.toSemanticTag;
    } else {
      nextEdits[fieldSignature].toSemanticTag = cleaned;
    }

    if (Object.keys(nextEdits[fieldSignature] || {}).length === 0) {
      delete nextEdits[fieldSignature];
    }

    const updatedSchema = updateSchemaWithEdits(schema, fieldSignature, { semanticTag: cleaned || fromTag || null });
    onDataChange({ lowConfidenceEdits: nextEdits, inferredSchema: updatedSchema });
  };

  const setMove = (fieldSignature: string, args: { toScreenKey?: string; toSectionKey?: string }) => {
    const cleanedScreen = args.toScreenKey !== undefined ? String(args.toScreenKey || '').trim() : undefined;
    const cleanedSection = args.toSectionKey !== undefined ? String(args.toSectionKey || '').trim() : undefined;

    const existing = edits?.[fieldSignature];
    const obj = typeof existing === 'object' && existing ? existing : {};
    const nextEdits = { ...edits, [fieldSignature]: { ...obj } };

    if (cleanedScreen !== undefined) {
      if (!cleanedScreen) {
        delete nextEdits[fieldSignature]?.toScreenKey;
      } else {
        nextEdits[fieldSignature].toScreenKey = cleanedScreen;
      }
    }

    if (cleanedSection !== undefined) {
      if (!cleanedSection) {
        delete nextEdits[fieldSignature]?.toSectionKey;
      } else {
        nextEdits[fieldSignature].toSectionKey = cleanedSection;
      }
    }

    if (Object.keys(nextEdits[fieldSignature] || {}).length === 0) {
      delete nextEdits[fieldSignature];
    }

    const updatedSchema = updateSchemaWithEdits(schema, fieldSignature, {
      screenKey: cleanedScreen ?? undefined,
      sectionKey: cleanedSection ?? undefined,
    });
    onDataChange({ lowConfidenceEdits: nextEdits, inferredSchema: updatedSchema });
  };

  if (!schema) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Review low confidence</h3>
          <p className="step-form-subtitle">No schema generated yet. Go back and extract fields.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Review low confidence</h3>
        <p className="step-form-subtitle">
          We’ve highlighted fields with low confidence. You can update their semantic tags (used for grouping and future learning).
        </p>
      </div>

      <div className="step-form-content">
        <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
          <div className="text-sm text-[#181818]">
            {rows.length} low-confidence fields • {changedCount} changes
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="mt-4 text-sm text-[#686E6C]">No low-confidence fields found.</div>
        ) : (
          <div className="mt-4 space-y-3">
            {rows.slice(0, 200).map((r) => (
              <div key={r.fieldSignature} className="rounded-xl border border-[#E6E6E4] bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#181818] truncate">{r.pdfFieldName || 'Field'}</div>
                    <div className="mt-1 text-xs text-[#686E6C]">Confidence: {Math.round(r.confidence * 100)}%</div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Current semantic tag</label>
                    <input className="form-input" type="text" value={r.fromSemanticTag || ''} disabled />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Updated semantic tag</label>
                    <input
                      className="form-input"
                      type="text"
                      value={String((edits?.[r.fieldSignature]?.toSemanticTag ?? r.fromSemanticTag) ?? '')}
                      onChange={(e) => setTag(r.fieldSignature, e.target.value)}
                      placeholder="e.g. engineer.name"
                    />
                    <div className="mt-1 text-[11px] text-[#686E6C]">
                      Leave unchanged to skip saving a correction.
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Screen key</label>
                    <input
                      className="form-input"
                      type="text"
                      value={String((edits?.[r.fieldSignature]?.toScreenKey ?? r.fromScreenKey) || '')}
                      onChange={(e) => setMove(r.fieldSignature, { toScreenKey: e.target.value })}
                      placeholder={r.fromScreenKey || 'e.g. engineer'}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Section key</label>
                    <input
                      className="form-input"
                      type="text"
                      value={String((edits?.[r.fieldSignature]?.toSectionKey ?? r.fromSectionKey) || '')}
                      onChange={(e) => setMove(r.fieldSignature, { toSectionKey: e.target.value })}
                      placeholder={r.fromSectionKey || 'e.g. engineer'}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
